import { z } from "zod";

import { DocumentDisplayUomSettingsSchema } from "~/shared/documents/schemas/document-display-uom.schema";
import { AllowTypedSignatureSettingsSchema } from "~/shared/documents/schemas/typed-signature.schema";
import { EditorSchema, type EditorSchemaType, emptyEditorDoc } from "~/utils/types/rich-text";

/** pickup-instructions slug — org settings at ORDER_DOCUMENT_CONFIG:pickup-instructions @see docs/OPT_IN_ORDER_DOCUMENTS.md */
export const PickupInstructionsDocumentConfigSchema = z
  .object({
    deliveryInstructionsHeader: EditorSchema,
    sealWashTicketBoilerplate: EditorSchema,
    demurrageBoilerplate: EditorSchema,
  })
  .merge(DocumentDisplayUomSettingsSchema)
  .merge(AllowTypedSignatureSettingsSchema);

export type PickupInstructionsDocumentConfig = z.infer<typeof PickupInstructionsDocumentConfigSchema>;

/** Pre-SC-2344 org settings stored a plain 24-hour phone string instead of deliveryInstructionsHeader. */
const LegacyPickupInstructionsDocumentConfigSchema = z.object({
  twentyFourHourPhone: z.string().trim().optional().default(""),
  sealWashTicketBoilerplate: EditorSchema,
  demurrageBoilerplate: EditorSchema,
});

const legacyTwentyFourHourPhoneToDeliveryInstructionsHeader = (phone: string): EditorSchemaType => {
  const label = "24 Hour Phone:";
  const text = `${label} ${phone}`;
  return {
    html: `<p><strong>${label}</strong> ${phone.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`,
    json: {
      type: "doc",
      content: [{ type: "paragraph", content: [{ type: "text", text }] }],
    },
    text,
  };
};

/** Phone-only rows from early Demo seed before boilerplate editors were required. */
const LegacyPhoneOnlyPickupInstructionsDocumentConfigSchema = z.object({
  twentyFourHourPhone: z.string().trim().min(1),
});

const migratePhoneOnlyPickupInstructionsDocumentConfig = (parsed: unknown): PickupInstructionsDocumentConfig | null => {
  const phoneOnly = LegacyPhoneOnlyPickupInstructionsDocumentConfigSchema.safeParse(parsed);
  if (!phoneOnly.success || typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const keys = Object.keys(parsed);
  if (keys.length !== 1 || keys[0] !== "twentyFourHourPhone") {
    return null;
  }
  return PickupInstructionsDocumentConfigSchema.parse({
    deliveryInstructionsHeader: legacyTwentyFourHourPhoneToDeliveryInstructionsHeader(phoneOnly.data.twentyFourHourPhone),
    sealWashTicketBoilerplate: emptyEditorDoc(),
    demurrageBoilerplate: emptyEditorDoc(),
  });
};

const migrateLegacyPickupInstructionsDocumentConfig = (parsed: unknown): PickupInstructionsDocumentConfig | null => {
  const legacy = LegacyPickupInstructionsDocumentConfigSchema.safeParse(parsed);
  if (!legacy.success) {
    return migratePhoneOnlyPickupInstructionsDocumentConfig(parsed);
  }
  const phone = legacy.data.twentyFourHourPhone.trim();
  return PickupInstructionsDocumentConfigSchema.parse({
    deliveryInstructionsHeader: phone ? legacyTwentyFourHourPhoneToDeliveryInstructionsHeader(phone) : emptyEditorDoc(),
    sealWashTicketBoilerplate: legacy.data.sealWashTicketBoilerplate,
    demurrageBoilerplate: legacy.data.demurrageBoilerplate,
  });
};

/** Accept current schema or migrate legacy twentyFourHourPhone rows on read. */
export const parsePickupInstructionsDocumentConfig = (parsed: unknown): PickupInstructionsDocumentConfig | null => {
  const current = PickupInstructionsDocumentConfigSchema.safeParse(parsed);
  if (current.success) {
    return current.data;
  }
  return migrateLegacyPickupInstructionsDocumentConfig(parsed);
};
